import { CheckBox, Toggle } from '@toggle/design-system';
import { Entity, formatTicker } from '@toggle/toggle';
import React, { ChangeEvent, FC, useMemo } from 'react';

import { EventsConfig } from '../../../types/events.types';
import * as S from './InsightsDropdown.styles';
import {
  handleAllInsightsToggle,
  handleAssetToggle,
  isAssetEnabled,
} from './utils';

export interface InsightsDropdownProps {
  assets: Entity[];
  eventsConfig: EventsConfig;
  onChange: (config: EventsConfig) => void;
  locales: {
    showInsights: string;
  };
}

export const InsightsDropdown: FC<InsightsDropdownProps> = ({
  assets,
  eventsConfig,
  onChange,
  locales,
}) => {
  const uniqueAssets = useMemo(() => {
    const seen = new Set<string>();
    return assets.filter(asset => {
      if (seen.has(asset.tag)) {
        return false;
      }

      seen.add(asset.tag);
      return true;
    });
  }, [assets]);

  const allInsightsEnabled = eventsConfig.TMI;

  return (
    <div>
      <S.AllInsightsRow>
        <S.AllInsightsLabel>{locales.showInsights}</S.AllInsightsLabel>
        <Toggle
          isChecked={allInsightsEnabled}
          onToggle={(checked: boolean) =>
            handleAllInsightsToggle({
              checked,
              uniqueAssets,
              eventsConfig,
              onChange,
            })
          }
          size="small"
          data-testid="all-insights-toggle"
        />
      </S.AllInsightsRow>
      <S.AssetList>
        {uniqueAssets.map(asset => {
          const ticker = formatTicker(asset);
          const entityName = asset.name_short || asset.name;
          return (
            <S.AssetRow key={asset.tag}>
              <S.CheckboxWrapper>
                <CheckBox
                  name={asset.tag}
                  label={ticker}
                  checked={isAssetEnabled({
                    tag: asset.tag,
                    eventsConfig,
                  })}
                  onChange={(_, e: ChangeEvent<HTMLInputElement>) =>
                    handleAssetToggle({
                      tag: asset.tag,
                      checked: e.target.checked,
                      eventsConfig,
                      uniqueAssets,
                      onChange,
                    })
                  }
                  size="small"
                  testId={`asset-checkbox-${asset.tag}`}
                />
              </S.CheckboxWrapper>
              <S.EntityName>{entityName}</S.EntityName>
            </S.AssetRow>
          );
        })}
      </S.AssetList>
    </div>
  );
};
